/**
 * One catalogue movement, as the table draws it.
 *
 * **The seam between the entity and `ui/`.** Everything a cell renders is decided here — which
 * photograph, which muscles in which order, how many pieces of equipment fit, whose it is — so no
 * component names a DTO field and every one of those decisions has one home.
 *
 * The translate function arrives as a parameter and i18next is never imported: `model/` is pure,
 * and the parameter is KEY-ONLY (`TranslateKey`), the same shape `pages/plan-library`'s projection
 * takes — nothing here interpolates a count, and a plural cannot travel through a key-only call
 * anyway. The only strings resolved here are the ones a SORT has to compare — a type's word and a
 * muscle's word — because ordering by a label the component would resolve later means the
 * projection and the comparator would disagree about what they are ordering.
 *
 * ── Muscle labels are the MOBILE locale's, in Title Case ────────────────────────────────────
 * Owner decision D13, 17 September 2026: „Klatka Piersiowa", not „Klatka piersiowa". The specimen
 * writes sentence case; the app writes what the phone writes, and there is no web-only muscle
 * dictionary. `muscleGroupLabelKey` is the entity's own key into `exerciseLibrary.muscles.*`.
 *
 * ── The pill CAP is not decided here, and the overflow is ───────────────────────────────────
 * The table draws two pills with the panel open and three without, so the cap is a property of the
 * layout rather than of the movement — and a projection that took it would re-run all 477 rows
 * every time the trainer pressed the toggle. The row carries every muscle it trains, in the canon's
 * order; `muscleOverflow` below is the decision about what a `+N` means and what it names, and the
 * cell calls it with the cap it is drawing at.
 */

import {
  muscleGroupLabelKey,
  stillUrl,
  type ExerciseListItem,
  type ExerciseTypeKey,
  type MuscleGroup,
} from '~/entities/exercise-library';

/**
 * i18next's `t`, narrowed to the only call this layer may make.
 *
 * A key in, a string out. Interpolation and pluralisation stay in `ui/`, which is where a count can
 * reach a real `t` — `pages/plan-library` states the same rule and for the same reason.
 */
export type TranslateKey = (key: string) => string;

/** How many equipment thumbs stand in a ROW before the rest become a `+N`. The canon's own three. */
export const EQUIPMENT_THUMBS = 3;

/** One muscle pill. `secondary` is the weaker treatment, not a different fact. */
export interface MusclePill {
  readonly group: MuscleGroup;
  readonly label: string;
  readonly secondary: boolean;
}

/** One equipment thumb. */
export interface EquipmentThumb {
  readonly id: number;
  /** Backend DATA, already localised. Never i18n. */
  readonly name: string;
  readonly imageUrl: string | null;
}

/** Whose movement it is. An `own` one wears the „Twoje" status after its name. */
export type ExerciseAuthor = 'library' | 'own';

export interface ExerciseRow {
  readonly id: number;
  /**
   * The row's handle for the keyboard walk and for React's reconciler. A plain string because
   * `usePointedRow` keys on strings, and there is only one list here — unlike the plan library,
   * which namespaces two id spaces that can collide.
   */
  readonly rowKey: string;
  /** Backend DATA, resolved into the reader's language. */
  readonly name: string;
  /** The poster, then the cover, then nothing. `entities/exercise-library` owns that order. */
  readonly stillUrl: string | null;
  readonly typeKey: ExerciseTypeKey | null;
  /** The type's word, drawn beside its glyph in the type's hue, and the Typ column's sort key. */
  readonly typeLabel: string;
  /** Primary first, then secondary, each in the taxonomy's own order. May be empty. */
  readonly muscles: readonly MusclePill[];
  /**
   * EVERY piece the movement names, in its own order. Uncapped, for the same reason the muscles
   * are: the table draws three and the preview panel draws all of them, so the cap is a property of
   * the surface rather than of the movement.
   */
  readonly equipment: readonly EquipmentThumb[];
  readonly author: ExerciseAuthor;
  /** Added from Buddy's proposal and not yet checked: the name cell says „Do uzupełnienia". */
  readonly needsReview: boolean;
  /**
   * The trainer's own tags, in the order the server keeps them. The table draws none (owner,
   * 4 November 2026); the search and the tag filter read them. Aliases are never on a row (R20).
   */
  readonly tags: readonly string[];
  /** What each sortable column compares. Collated by `exerciseSort`, never by `<`. */
  readonly sortKeys: ExerciseSortKeys;
}

/**
 * One string per sortable column.
 *
 * Strings rather than the values themselves, because every one of the four is ordered by what the
 * trainer READS — a type by its word, equipment by its name — and a comparator that sorted by an
 * enum's declaration order would put the columns in an order nothing on screen explains. The Autor
 * column, and the bucket it sorted by, went with the polish of 4 November 2026: whose movement it is
 * now reads as the „Twoje" status after its name, which is not a sort.
 */
export interface ExerciseSortKeys {
  readonly name: string;
  readonly type: string;
  readonly muscles: string;
  readonly equipment: string;
}

export function toExerciseRow(item: ExerciseListItem, translate: TranslateKey): ExerciseRow {
  const muscles: MusclePill[] = [
    ...item.primaryMuscles.map((group) => ({
      group,
      label: translate(muscleGroupLabelKey(group)),
      secondary: false,
    })),
    ...item.secondaryMuscles.map((group) => ({
      group,
      label: translate(muscleGroupLabelKey(group)),
      secondary: true,
    })),
  ];
  // `web:` and not the upstream group: the mobile locale has six type labels and no `custom`, so
  // the seventh would have to come from another namespace. One legend, one namespace.
  const typeLabel =
    item.typeKey === null
      ? translate('web:exerciseLibrary.type.unknown')
      : translate(`web:exerciseLibrary.type.${item.typeKey}`);

  return {
    id: item.id,
    rowKey: String(item.id),
    name: item.name,
    stillUrl: stillUrl(item),
    typeKey: item.typeKey,
    typeLabel,
    muscles,
    equipment: item.equipment,
    author: item.isCustom ? 'own' : 'library',
    needsReview: item.needsReview,
    tags: item.tags,
    sortKeys: {
      name: item.name,
      type: typeLabel,
      // The LEADING muscle, which is what the trainer sees in the first pill. A row that trains
      // nothing the catalogue knows sorts to the end rather than to the front, which is why the
      // fallback is not the empty string.
      muscles: muscles[0]?.label ?? '￿',
      equipment: item.equipment[0]?.name ?? '￿',
    },
  };
}

export function toExerciseRows(
  items: readonly ExerciseListItem[],
  translate: TranslateKey,
): ExerciseRow[] {
  return items.map((item) => toExerciseRow(item, translate));
}

/** What a muscle cell draws at a given cap. */
export interface MuscleOverflow {
  readonly shown: readonly MusclePill[];
  /** How many did not fit. `0` when they all did. */
  readonly hidden: number;
  /** The hidden ones, named — the `+N` mark's own title and its accessible text. */
  readonly hiddenNames: readonly string[];
}

/**
 * Which pills fit, and what the `+N` beside them stands for.
 *
 * The cap comes from the layout: two with the preview panel open, three without. A `+N` with no
 * words behind it is a number a trainer cannot act on, so the hidden names travel with it —
 * `entities/exercise-library` keeps primary before secondary, so what is dropped is always the
 * least relevant end of the list.
 */
export function muscleOverflow(
  muscles: readonly MusclePill[],
  cap: number,
): MuscleOverflow {
  const shown = muscles.slice(0, cap);
  const hidden = muscles.slice(cap);
  return { shown, hidden: hidden.length, hiddenNames: hidden.map((pill) => pill.label) };
}

/** A movement's status, drawn as a small neutral chip after its name. */
export type ExerciseRowStatus = 'own' | 'review';

/**
 * The statuses a row's name cell prints after the name, „Twoje" before „Do uzupełnienia".
 *
 * Both say something about the movement itself — whose it is, whether it still needs a look — and
 * the name gives way to them rather than the other way round. The trainer's own TAGS are not here:
 * the table draws none (owner, 4 November 2026); they live in the bar above it and in the card.
 */
export function exerciseRowStatuses(
  row: Pick<ExerciseRow, 'author' | 'needsReview'>,
): readonly ExerciseRowStatus[] {
  const statuses: ExerciseRowStatus[] = [];
  if (row.author === 'own') statuses.push('own');
  if (row.needsReview) statuses.push('review');
  return statuses;
}
